import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CollapsibleTopPanel from '../../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import {
  approveUnitRequest,
  assignTenantToUnit,
  assignRentalManual,
  confirmUnitRequestPayments,
  getPropertyById,
  getPropertyTenancyHistory,
  getPropertyUnitRequests,
  findPropertyUnitRequestById,
  isUnitRequestPendingForReject,
  rejectUnitRequest,
  rejectUnitRequestPaymentEvidence,
  searchTenantsByIdentifier,
  getTenantScoreByUserId,
  getTenantScoreByUserIdAsAgent,
} from '../../../../api/services/propertyService';
import { fetchAuthenticatedImage } from '../../../../api/services/authService';
import {
  getTenantUserIdFromUnitRequest,
} from '../../../../utils/tenantScoreAccess';
import { applyKycGuestFallback, resolveKycDocUrls } from '../../../../utils/kycDocFallback';
import { resolveUnitRequestIdFromHint } from '../../../shared/Notifications/notificationUtils';
import GuestChatModal from '../../../shared/GuestChatModal/GuestChatModal';
import {
  MdApartment,
  MdCalendarToday,
  MdAttachMoney,
  MdPerson,
  MdEmail,
  MdBusiness,
  MdChat,
} from 'react-icons/md';
import './PropertyUnitRequestsView.css';

function formatMoneyKES(amount) {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  return `KES ${amount.toLocaleString()}`;
}

function formatDate(isoString) {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

function formatUnitRequestStatus(status) {
  if (!status) return '—';
  return String(status).replace(/_/g, ' ');
}

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'MANUAL', label: 'Manually booked' },
  { value: 'REJECTED', label: 'Rejected' },
];

function parseList(res) {
  const d = res?.data;
  return Array.isArray(d) ? d : [];
}

function getRequesterName(req) {
  const name = [req?.requesterFirstName, req?.requesterLastName].filter(Boolean).join(' ');
  return name || 'Tenant';
}

const AuthenticatedImage = ({ src, alt, className, style, onClick }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadingImg, setLoadingImg] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!src) return;
      try {
        setLoadingImg(true);
        setError(false);
        const url = await fetchAuthenticatedImage(src);
        if (active) setBlobUrl(url || src);
      } catch {
        if (active) setError(true);
      } finally {
        if (active) setLoadingImg(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [src]);

  if (!src) return null;

  if (loadingImg) {
    return (
      <div
        className={className}
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8fafc',
        }}
      />
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={className} style={{ ...style, background: '#f8fafc', color: '#64748b', fontSize: 12 }}>
        Preview unavailable
      </div>
    );
  }

  return (
    <img
      src={blobUrl}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
    />
  );
};

/**
 * Transforms a Tenancy History item into a Unit Request shape for UI consistency.
 */
const transformTenancyToRequest = (t) => {
  const requesterUserId = t.tenantUserId || t.assignedUserId || t.userId || null;
  return {
  ...t,
  requesterFirstName: t.userFirstName,
  requesterLastName: t.userLastName,
  requesterEmail: t.userEmail,
  requesterUserId,
  guestUserId: requesterUserId,
  requesterTenantCode: t.tenantCode,
  requesterKycStatus: t.userKycStatus,
  requesterKycIdDocumentUrl: t.userKycIdDocumentUrl,
  requesterKycIdDocumentBackUrl: t.userKycIdDocumentBackUrl,
  requesterKycSelfieUrl: t.userKycSelfieUrl,
  status: 'MANUAL',
  };
};

/**
 * @param {{ id: string, name: string }[]} properties
 * @param {boolean} loadingProperties
 * @param {string} pageTitle
 * @param {string} [emptyPropertiesMessage]
 * @param {string} [defaultPropertyId] - Pre-select property when present in list (e.g. from card shortcut)
 */
const PropertyUnitRequestsView = ({
  properties = [],
  loadingProperties = false,
  pageTitle = 'Unit requests',
  emptyPropertiesMessage = 'No properties available.',
  defaultPropertyId = null,
  variant = 'landlord',
  onNavigate,
  highlightRequestId = null,
  focusRequestOnly = false,
  openPaymentEvidence = false,
  requestResolveHint = null,
  onFocusExit,
}) => {
  const [selectedPropertyId, setSelectedPropertyId] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tenantCodeFilter, setTenantCodeFilter] = useState('');
  const [requests, setRequests] = useState([]);
  const [kycSourceRequests, setKycSourceRequests] = useState([]);
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);
  const [activeFocusRequestId, setActiveFocusRequestId] = useState(null);
  const focusHandledRef = React.useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionRequestId, setActionRequestId] = useState(null);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectReasonById, setRejectReasonById] = useState({});
  const [confirmingPaymentsRequestId, setConfirmingPaymentsRequestId] = useState(null);
  const [confirmPaymentsById, setConfirmPaymentsById] = useState({});
  const [approveConfirm, setApproveConfirm] = useState({ open: false, req: null, message: '' });
  const [assignTenantModal, setAssignTenantModal] = useState({ open: false, req: null, userId: '', startDate: '' });
  const [kycModal, setKycModal] = useState({ open: false, req: null });
  const [kycImageUrls, setKycImageUrls] = useState({});
  const [kycImagesLoading, setKycImagesLoading] = useState(false);
  const [manualAssignModal, setManualAssignModal] = useState({
    open: false,
    propertyId: '',
    unitId: '',
    userId: '',
    startDate: '',
    amountPaid: '',
  });
  const [manualAssignUnitsByProperty, setManualAssignUnitsByProperty] = useState({});
  const [manualAssignLoadingUnits, setManualAssignLoadingUnits] = useState(false);
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [tenantSearchLoading, setTenantSearchLoading] = useState(false);
  const [tenantSearchResults, setTenantSearchResults] = useState([]);
  const [tenantSearchError, setTenantSearchError] = useState('');

  const [scoreModal, setScoreModal] = useState({
    open: false,
    req: null,
    score: null, // response data
    loading: false,
    error: '',
    monthsBack: 12,
  });
  const [evidenceModalRequest, setEvidenceModalRequest] = useState(null);
  const [evidenceModalError, setEvidenceModalError] = useState('');
  const [chatRequest, setChatRequest] = useState(null);

  const propertyById = useMemo(() => {
    const m = {};
    properties.forEach((p) => {
      if (p?.id) m[p.id] = p.name || 'Property';
    });
    return m;
  }, [properties]);

  const enrichedRequests = useMemo(
    () => applyKycGuestFallback(requests, kycSourceRequests, getTenantUserIdFromUnitRequest),
    [requests, kycSourceRequests]
  );

  const filteredRequests = useMemo(() => {
    const code = String(tenantCodeFilter || '').trim().toLowerCase();
    if (!code) return enrichedRequests;
    return enrichedRequests.filter((req) =>
      String(req?.requesterTenantCode || '').toLowerCase().includes(code)
    );
  }, [enrichedRequests, tenantCodeFilter]);

  useEffect(() => {
    setHighlightedRequestId(highlightRequestId ? String(highlightRequestId) : null);
    setActiveFocusRequestId(
      focusRequestOnly && highlightRequestId ? String(highlightRequestId) : null
    );
    focusHandledRef.current = null;
  }, [highlightRequestId, requestResolveHint, focusRequestOnly]);

  const isFocusMode = Boolean(focusRequestOnly && activeFocusRequestId);

  const displayRequests = useMemo(() => {
    if (!isFocusMode) return filteredRequests;
    return filteredRequests.filter((r) => String(r.id) === String(activeFocusRequestId));
  }, [filteredRequests, isFocusMode, activeFocusRequestId]);

  const exitFocusMode = useCallback(() => {
    setActiveFocusRequestId(null);
    setHighlightedRequestId(null);
    if (typeof onFocusExit === 'function') {
      onFocusExit();
    }
  }, [onFocusExit]);

  const fetchKycSourceRequests = useCallback(
    async (statusParam) => {
      if (!statusParam) {
        setKycSourceRequests([]);
        return;
      }
      try {
        if (selectedPropertyId && selectedPropertyId !== 'all') {
          const res = await getPropertyUnitRequests(selectedPropertyId, { status: 'all' });
          setKycSourceRequests(parseList(res));
          return;
        }
        const results = await Promise.all(
          properties.map((p) =>
            getPropertyUnitRequests(p.id, { status: 'all' }).then(parseList).catch(() => [])
          )
        );
        setKycSourceRequests(results.flat());
      } catch {
        setKycSourceRequests([]);
      }
    },
    [properties, selectedPropertyId]
  );

  const loadRequests = useCallback(async () => {
    if (!properties.length) {
      setRequests([]);
      setKycSourceRequests([]);
      return;
    }
    setLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      if (statusFilter === 'MANUAL') {
        setKycSourceRequests([]);
        if (selectedPropertyId && selectedPropertyId !== 'all') {
          const res = await getPropertyTenancyHistory(selectedPropertyId);
          setRequests(parseList(res).map(transformTenancyToRequest));
          return;
        }
        const results = await Promise.all(
          properties.map((p) => getPropertyTenancyHistory(p.id).then(parseList).catch(() => []))
        );
        const merged = results.flat().map(transformTenancyToRequest);
        merged.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setRequests(merged);
        return;
      }

      const statusParam = statusFilter === 'all' ? undefined : statusFilter;

      if (selectedPropertyId && selectedPropertyId !== 'all') {
        const [res] = await Promise.all([
          getPropertyUnitRequests(selectedPropertyId, {
            status: statusParam || 'all',
          }),
          fetchKycSourceRequests(statusParam),
        ]);
        setRequests(parseList(res));
        return;
      }

      const [results] = await Promise.all([
        Promise.all(
          properties.map((p) =>
            getPropertyUnitRequests(p.id, { status: statusParam || 'all' }).then(parseList).catch(() => [])
          )
        ),
        fetchKycSourceRequests(statusParam),
      ]);

      // Fetch manual assignments from tenancy history if we are in 'all' view
      let manualRecords = [];
      if (statusFilter === 'all') {
        if (selectedPropertyId && selectedPropertyId !== 'all') {
          const historyRes = await getPropertyTenancyHistory(selectedPropertyId).then(parseList).catch(() => []);
          manualRecords = historyRes.map(transformTenancyToRequest);
        } else {
          const historyResults = await Promise.all(
            properties.map((p) => getPropertyTenancyHistory(p.id).then(parseList).catch(() => []))
          );
          manualRecords = historyResults.flat().map(transformTenancyToRequest);
        }
      }

      const merged = [];
      const seenIds = new Set();
      const seenUnits = new Set(); // Track units to avoid showing multiple active/assigned records for the same unit

      // First add the results from unit requests
      results.flat().forEach((r) => {
        if (r?.id && !seenIds.has(r.id)) {
          seenIds.add(r.id);
          merged.push(r);
          if (r.status === 'ASSIGNED' && r.unitId) {
            seenUnits.add(r.unitId);
          }
        }
      });

      // Add manual records that aren't already represented by an assigned request for the same unit
      manualRecords.forEach((m) => {
        if (!seenIds.has(m.id) && !seenUnits.has(m.unitId)) {
          seenIds.add(m.id);
          merged.push(m);
        }
      });

      merged.sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime();
        const tb = new Date(b.createdAt || 0).getTime();
        return tb - ta;
      });
      setRequests(merged);
    } catch (e) {
      setError(e?.message || 'Failed to load requests.');
      setRequests([]);
      setKycSourceRequests([]);
    } finally {
      setLoading(false);
    }
  }, [properties, selectedPropertyId, statusFilter, fetchKycSourceRequests]);

  const handleApprove = useCallback(
    async (req) => {
      if (!req?.propertyId || !req?.id) return;
      const tenantName =
        [req.requesterFirstName, req.requesterLastName].filter(Boolean).join(' ') || 'this tenant';
      const unitName = req.unitName || req.unitNumber || 'this unit';
      setApproveConfirm({
        open: true,
        req,
        message: `Approve request for ${tenantName} to rent ${unitName}?`,
      });
    },
    []
  );

  const cancelApproveConfirm = useCallback(() => {
    setApproveConfirm({ open: false, req: null, message: '' });
  }, []);

  const confirmApprove = useCallback(async () => {
    const req = approveConfirm?.req;
    if (!req?.propertyId || !req?.id) return;

    setActionRequestId(req.id);
    setError('');
    setSuccessMessage('');
    try {
      const res = await approveUnitRequest(req.propertyId, req.id);
      setSuccessMessage(res?.message || 'Request approved.');
      cancelApproveConfirm();
      await loadRequests();
    } catch (e) {
      setError(e?.message || 'Failed to approve request.');
    } finally {
      setActionRequestId(null);
    }
  }, [approveConfirm?.req, cancelApproveConfirm, loadRequests]);

  const openRejectPanel = useCallback((req) => {
    if (!req?.id) return;
    setRejectingRequestId(req.id);
    setError('');
    setSuccessMessage('');
    setRejectReasonById((prev) => (prev[req.id] == null ? { ...prev, [req.id]: '' } : prev));
  }, []);

  const cancelReject = useCallback(() => {
    setRejectingRequestId(null);
  }, []);

  const requestHasPaymentEvidence = useCallback(
    (r) =>
      Boolean(
        r?.depositEvidenceUrl ||
        r?.depositEvidenceText ||
        r?.depositPaidAmount ||
        r?.firstMonthEvidenceUrl ||
        r?.firstMonthEvidenceText ||
        r?.firstMonthPaidAmount
      ),
    []
  );

  const confirmReject = useCallback(
    async (req) => {
      if (!req?.propertyId || !req?.id) return;
      const reason = rejectReasonById?.[req.id] || '';

      setActionRequestId(req.id);
      setError('');
      setSuccessMessage('');
      try {
        let res;
        if (isUnitRequestPendingForReject(req)) {
          res = await rejectUnitRequest(req.propertyId, req.id, { reason });
          setSuccessMessage(res?.message || 'Request rejected.');
        } else if (requestHasPaymentEvidence(req)) {
          res = await rejectUnitRequestPaymentEvidence(req.propertyId, req, {
            reason,
            properties,
          });
          setSuccessMessage(res?.message || 'Payment evidence rejected. The tenant can resubmit.');
        } else {
          throw new Error('This request can no longer be rejected.');
        }
        setRejectingRequestId(null);
        await loadRequests();
      } catch (e) {
        setError(e?.message || 'Failed to reject request.');
      } finally {
        setActionRequestId(null);
      }
    },
    [loadRequests, properties, rejectReasonById, requestHasPaymentEvidence]
  );

  const openConfirmPayments = useCallback((req) => {
    if (!req?.id) return;
    setConfirmingPaymentsRequestId(req.id);
    setError('');
    setSuccessMessage('');
    setConfirmPaymentsById((prev) => {
      if (prev[req.id]) return prev;
      const hasDepositEvidence = Boolean(req.depositEvidenceUrl || req.depositEvidenceText || req.depositPaidAmount);
      const hasFirstEvidence = Boolean(req.firstMonthEvidenceUrl || req.firstMonthEvidenceText || req.firstMonthPaidAmount);
      return {
        ...prev,
        [req.id]: {
          depositPaid: hasDepositEvidence ? true : Boolean(req.depositConfirmed),
          firstMonthPaid: hasFirstEvidence ? true : Boolean(req.firstMonthConfirmed),
        },
      };
    });
  }, []);

  const cancelConfirmPayments = useCallback(() => {
    setConfirmingPaymentsRequestId(null);
  }, []);

  const paymentsFullyConfirmed = useCallback(
    (req) => Boolean(req?.depositConfirmed) && Boolean(req?.firstMonthConfirmed),
    []
  );

  const openEvidenceModal = useCallback((req) => {
    setEvidenceModalError('');
    setEvidenceModalRequest(req);
  }, []);

  const closeEvidenceModal = useCallback(() => {
    setEvidenceModalRequest(null);
    setEvidenceModalError('');
  }, []);

  const handleEvidenceReject = useCallback(() => {
    const req = evidenceModalRequest;
    if (!req?.id) return;
    closeEvidenceModal();
    openRejectPanel(req);
  }, [closeEvidenceModal, evidenceModalRequest, openRejectPanel]);

  const handleEvidenceConfirmPayments = useCallback(() => {
    const req = evidenceModalRequest;
    if (!req?.id) return;
    closeEvidenceModal();
    openConfirmPayments(req);
  }, [closeEvidenceModal, evidenceModalRequest, openConfirmPayments]);

  const handleEvidenceApprove = useCallback(() => {
    const req = evidenceModalRequest;
    if (!req?.id) return;
    closeEvidenceModal();
    handleApprove(req);
  }, [closeEvidenceModal, evidenceModalRequest, handleApprove]);

  const openProtectedFile = useCallback(
    async (e, url, title = 'Preview') => {
      e.preventDefault();
      e.stopPropagation();
      if (!url) return;
      setError('');

      const newWindow = window.open('', '_blank');
      if (newWindow) {
        try {
          newWindow.document.title = title;
          newWindow.document.body.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
          newWindow.document.body.style.padding = '16px';
          newWindow.document.body.textContent = `Loading ${title.toLowerCase()}…`;
        } catch {
          /* ignore */
        }
      }

      try {
        const blobUrl = await fetchAuthenticatedImage(url);
        const targetUrl = blobUrl || url;
        if (newWindow) {
          try {
            newWindow.opener = null;
          } catch {
            /* ignore */
          }
          newWindow.location.href = targetUrl;
        } else {
          window.open(targetUrl, '_blank');
        }
      } catch (err) {
        if (newWindow) newWindow.close();
        setError(err?.message || `Failed to open ${title.toLowerCase()}.`);
      }
    },
    []
  );

  const submitConfirmPayments = useCallback(
    async (req) => {
      if (!req?.propertyId || !req?.id) return;
      const payload = confirmPaymentsById?.[req.id];
      if (!payload) return;

      setActionRequestId(req.id);
      setError('');
      setSuccessMessage('');
      try {
        const res = await confirmUnitRequestPayments(req.propertyId, req.id, payload);
        setSuccessMessage(res?.message || 'Payments confirmed.');
        setConfirmingPaymentsRequestId(null);
        await loadRequests();
      } catch (e) {
        setError(e?.message || 'Failed to confirm payments.');
      } finally {
        setActionRequestId(null);
      }
    },
    [confirmPaymentsById, loadRequests]
  );

  const getUnitIdFromRequest = useCallback((req) => {
    return (
      req?.unitId || req?.unitID || req?.unit?.id || req?.unit?.unitId || req?.propertyUnitId || req?.propertyUnitID || null
    );
  }, []);

  const getRequesterUserIdFromRequest = useCallback((req) => {
    return getTenantUserIdFromUnitRequest(req);
  }, []);

  const getPropertyIdFromRequest = useCallback((req) => {
    return req?.propertyId || req?.propertyID || req?.property?.id || null;
  }, []);

  const openChatModal = useCallback(
    (req) => {
      const propertyId = getPropertyIdFromRequest(req);
      const tenantUserId = getRequesterUserIdFromRequest(req);
      if (!propertyId || !tenantUserId) {
        setError('Cannot open chat: missing tenant or property details.');
        return;
      }
      setChatRequest(req);
    },
    [getPropertyIdFromRequest, getRequesterUserIdFromRequest]
  );

  const handleViewScore = useCallback(
    async (req, months = 12) => {
      const tenantUserId = getTenantUserIdFromUnitRequest(req);
      if (!tenantUserId) {
        setError('Cannot view score: missing user ID on this request.');
        return;
      }

      setScoreModal({
        open: true,
        req,
        score: null,
        loading: true,
        error: '',
        monthsBack: months,
      });

      try {
        const res =
          variant === 'agent'
            ? await getTenantScoreByUserIdAsAgent(tenantUserId, months, { propertyId: req.propertyId })
            : await getTenantScoreByUserId(tenantUserId, months);

        setScoreModal((prev) => ({
          ...prev,
          score: res?.data || res,
          loading: false,
        }));
      } catch (e) {
        console.error('View score error:', e);
        setScoreModal((prev) => ({
          ...prev,
          loading: false,
          error: e?.message || 'Failed to load tenant score.',
        }));
      }
    },
    [variant]
  );

  const closeScoreModal = useCallback(() => {
    setScoreModal((prev) => ({ ...prev, open: false, req: null, score: null, loading: false }));
  }, []);

  const openKycModal = useCallback(async (req) => {
    setKycModal({ open: true, req });
    const reqId = req.id;
    setKycImageUrls((prev) => {
      if (prev[reqId]) return prev;
      return prev;
    });
    if (kycImageUrls[reqId]) return;
    setKycImagesLoading(true);
    try {
      const { front: frontUrl, back: backUrl, selfie: selfieUrl } = resolveKycDocUrls(req);
      const [front, back, selfie] = await Promise.all([
        frontUrl
          ? fetchAuthenticatedImage(frontUrl).catch(() => frontUrl)
          : Promise.resolve(null),
        backUrl
          ? fetchAuthenticatedImage(backUrl).catch(() => backUrl)
          : Promise.resolve(null),
        selfieUrl
          ? fetchAuthenticatedImage(selfieUrl).catch(() => selfieUrl)
          : Promise.resolve(null),
      ]);
      setKycImageUrls((prev) => ({ ...prev, [reqId]: { front, back, selfie } }));
    } catch {
      const { front, back, selfie } = resolveKycDocUrls(req);
      setKycImageUrls((prev) => ({
        ...prev,
        [reqId]: { front, back, selfie },
      }));
    } finally {
      setKycImagesLoading(false);
    }
  }, [kycImageUrls]);

  const closeKycModal = useCallback(() => {
    setKycModal({ open: false, req: null });
  }, []);

  const openAssignTenant = useCallback(
    (req) => {
      if (!req?.propertyId) return;
      if (req?.assignedAt || String(req?.status || '').toUpperCase() === 'ASSIGNED') {
        setError('This unit already has an active tenant assignment. End the current tenancy first.');
        return;
      }
      const unitId = getUnitIdFromRequest(req);
      if (!unitId) {
        setError('Cannot assign tenant: missing unitId on this request.');
        return;
      }
      setError('');
      setSuccessMessage('');
      const prefillUserId = getRequesterUserIdFromRequest(req) || '';
      if (!prefillUserId) {
        setError('Cannot assign tenant: missing requesterUserId on this request.');
        return;
      }
      setAssignTenantModal({ open: true, req, userId: prefillUserId, startDate: '' });
    },
    [getRequesterUserIdFromRequest, getUnitIdFromRequest]
  );

  const closeAssignTenant = useCallback(() => {
    setAssignTenantModal({ open: false, req: null, userId: '', startDate: '' });
  }, []);

  const normalizeUnitOptions = useCallback((propertyPayload) => {
    const unitsRaw = Array.isArray(propertyPayload?.units)
      ? propertyPayload.units
      : Array.isArray(propertyPayload?.data?.units)
        ? propertyPayload.data.units
        : Array.isArray(propertyPayload?.data)
          ? propertyPayload.data
          : [];
    return unitsRaw
      .map((unit, index) => {
        const id = unit?.id || unit?.unitId || unit?.propertyUnitId || null;
        if (!id) return null;
        const name = unit?.unitNumber || unit?.name || unit?.unitName || `Unit ${index + 1}`;
        const status = String(unit?.status || '').toUpperCase();
        return {
          id: String(id),
          name,
          status,
        };
      })
      .filter(Boolean);
  }, []);

  const openManualAssignModal = useCallback(() => {
    const initialPropertyId =
      selectedPropertyId && selectedPropertyId !== 'all'
        ? selectedPropertyId
        : (properties[0]?.id || '');
    if (!initialPropertyId) {
      setError('No property available for manual assignment.');
      return;
    }
    setError('');
    setSuccessMessage('');
    setManualAssignModal({
      open: true,
      propertyId: String(initialPropertyId),
      unitId: '',
      userId: '',
      startDate: '',
      amountPaid: '',
    });
    setTenantSearchQuery('');
    setTenantSearchResults([]);
    setTenantSearchError('');
  }, [properties, selectedPropertyId]);

  const closeManualAssignModal = useCallback(() => {
    setManualAssignModal({
      open: false,
      propertyId: '',
      unitId: '',
      userId: '',
      startDate: '',
      amountPaid: '',
    });
    setTenantSearchQuery('');
    setTenantSearchResults([]);
    setTenantSearchError('');
  }, []);

  useEffect(() => {
    if (!manualAssignModal.open) return;
    const query = String(tenantSearchQuery || '').trim();
    if (manualAssignModal.userId) {
      setTenantSearchError('');
      return;
    }
    if (!query) {
      setTenantSearchResults([]);
      setTenantSearchError('');
      return;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setTenantSearchLoading(true);
      setTenantSearchError('');
      try {
        const res = await searchTenantsByIdentifier(query);
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setTenantSearchResults(rows);
        }
      } catch (e) {
        if (!cancelled) {
          setTenantSearchResults([]);
          setTenantSearchError(e?.message || 'Failed to search tenants.');
        }
      } finally {
        if (!cancelled) {
          setTenantSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [manualAssignModal.open, manualAssignModal.userId, tenantSearchQuery]);

  useEffect(() => {
    if (!manualAssignModal.open || !manualAssignModal.propertyId) return;
    const propertyId = String(manualAssignModal.propertyId);
    if (Array.isArray(manualAssignUnitsByProperty[propertyId])) return;

    let cancelled = false;
    const loadUnits = async () => {
      setManualAssignLoadingUnits(true);
      try {
        const res = await getPropertyById(propertyId, true);
        const normalized = normalizeUnitOptions(res);
        if (!cancelled) {
          setManualAssignUnitsByProperty((prev) => ({ ...prev, [propertyId]: normalized }));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load units for selected property.');
          setManualAssignUnitsByProperty((prev) => ({ ...prev, [propertyId]: [] }));
        }
      } finally {
        if (!cancelled) setManualAssignLoadingUnits(false);
      }
    };
    loadUnits();
    return () => {
      cancelled = true;
    };
  }, [manualAssignModal.open, manualAssignModal.propertyId, manualAssignUnitsByProperty, normalizeUnitOptions]);

  const submitManualAssign = useCallback(async () => {
    const propertyId = String(manualAssignModal.propertyId || '').trim();
    const unitId = String(manualAssignModal.unitId || '').trim();
    const userId = String(manualAssignModal.userId || '').trim();
    const startDate = String(manualAssignModal.startDate || '').trim();
    const amountPaid = manualAssignModal.amountPaid ? Number(manualAssignModal.amountPaid) : 0;

    if (!propertyId) {
      setError('Select a property first.');
      return;
    }
    if (!unitId) {
      setError('Select a unit first.');
      return;
    }
    if (!userId) {
      setError('Tenant user ID is required.');
      return;
    }

    const requestId = 'manual-assign';
    setActionRequestId(requestId);
    setError('');
    setSuccessMessage('');
    try {
      const payload = {
        userId,
        ...(startDate ? { startDate } : {}),
        amountPaid,
        agreementAccepted: true,
      };
      const res = await assignRentalManual(propertyId, unitId, payload);
      setSuccessMessage(res?.message || 'Tenant assigned manually.');
      closeManualAssignModal();
      await loadRequests();
    } catch (e) {
      setError(e?.message || 'Failed to assign tenant manually.');
    } finally {
      setActionRequestId(null);
    }
  }, [closeManualAssignModal, loadRequests, manualAssignModal.propertyId, manualAssignModal.startDate, manualAssignModal.amountPaid, manualAssignModal.unitId, manualAssignModal.userId]);

  const submitAssignTenant = useCallback(async () => {
    const req = assignTenantModal.req;
    if (!req?.propertyId) return;
    const unitId = getUnitIdFromRequest(req);
    if (!unitId) {
      setError('Cannot assign tenant: missing unitId on this request.');
      return;
    }
    const userId = String(assignTenantModal.userId || '').trim();
    if (!userId) {
      setError('Cannot assign tenant: missing requesterUserId on this request.');
      return;
    }

    setActionRequestId(req.id);
    setError('');
    setSuccessMessage('');
    try {
      const payload = {
        userId,
        ...(assignTenantModal.startDate && String(assignTenantModal.startDate).trim()
          ? { startDate: String(assignTenantModal.startDate).trim() }
          : {}),
        agreementAccepted: true,
      };
      const res = await assignTenantToUnit(req.propertyId, unitId, payload);
      setSuccessMessage(res?.message || 'Tenant assigned to unit.');
      closeAssignTenant();
      await loadRequests();
    } catch (e) {
      setError(e?.message || 'Failed to assign tenant to unit.');
    } finally {
      setActionRequestId(null);
    }
  }, [assignTenantModal.req, assignTenantModal.startDate, assignTenantModal.userId, closeAssignTenant, getUnitIdFromRequest, loadRequests]);

  const confirmingPaymentsReq = useMemo(() => {
    if (!confirmingPaymentsRequestId) return null;
    return enrichedRequests.find((r) => r?.id === confirmingPaymentsRequestId) || null;
  }, [confirmingPaymentsRequestId, enrichedRequests]);

  const rejectingReq = useMemo(() => {
    if (!rejectingRequestId) return null;
    return enrichedRequests.find((r) => r?.id === rejectingRequestId) || null;
  }, [rejectingRequestId, enrichedRequests]);

  const showRejectModal = Boolean(rejectingReq);

  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape' && confirmingPaymentsRequestId) {
        cancelConfirmPayments();
      }
      if (ev.key === 'Escape' && evidenceModalRequest) {
        closeEvidenceModal();
      }
      if (ev.key === 'Escape' && showRejectModal) {
        cancelReject();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cancelConfirmPayments, cancelReject, closeEvidenceModal, confirmingPaymentsRequestId, evidenceModalRequest, showRejectModal]);

  useEffect(() => {
    if (loadingProperties) return;
    loadRequests();
  }, [loadingProperties, loadRequests]);

  useEffect(() => {
    if (loadingProperties || !defaultPropertyId || !properties.length) return;
    const exists = properties.some((p) => p.id === defaultPropertyId);
    if (exists) setSelectedPropertyId(defaultPropertyId);
  }, [loadingProperties, defaultPropertyId, properties]);

  useEffect(() => {
    if (loadingProperties) return;
    if (!highlightRequestId && !requestResolveHint && !focusRequestOnly) return;

    let cancelled = false;

    const applyFocusToRequest = (req, targetId) => {
      if (!req || cancelled) return;

      const handleKey = `${targetId}:${openPaymentEvidence}`;
      if (focusHandledRef.current === handleKey) return;
      focusHandledRef.current = handleKey;

      setStatusFilter('all');
      setTenantCodeFilter('');
      if (req.propertyId) {
        setSelectedPropertyId(req.propertyId);
      }
      setHighlightedRequestId(targetId);
      if (focusRequestOnly) {
        setActiveFocusRequestId(targetId);
      }

      window.setTimeout(() => {
        if (cancelled) return;
        const el = document.querySelector(`[data-request-id="${targetId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);

      window.setTimeout(() => {
        if (cancelled) return;
        if (openPaymentEvidence && requestHasPaymentEvidence(req)) {
          openEvidenceModal(req);
        }
      }, 280);
    };

    (async () => {
      setStatusFilter('all');

      let targetId = highlightRequestId ? String(highlightRequestId) : null;
      const idMatches = (r, id) => String(r?.id).toLowerCase() === String(id).toLowerCase();
      let req = targetId ? enrichedRequests.find((r) => idMatches(r, targetId)) : null;

      if (!req && targetId && properties.length > 0) {
        const fetched = await findPropertyUnitRequestById(properties, targetId);
        if (fetched && !cancelled) {
          req = fetched;
          setRequests((prev) => {
            if (prev.some((r) => String(r.id) === targetId)) return prev;
            return [fetched, ...prev];
          });
        }
      }

      if (!req && !targetId && requestResolveHint && properties.length > 0) {
        const results = await Promise.all(
          properties.map((p) =>
            getPropertyUnitRequests(p.id, { status: 'all' }).then(parseList).catch(() => [])
          )
        );
        const allRequests = results.flat();
        targetId = resolveUnitRequestIdFromHint(allRequests, requestResolveHint);
        if (targetId) {
          req =
            allRequests.find((r) => idMatches(r, targetId)) ||
            (await findPropertyUnitRequestById(properties, targetId));
          if (req && !cancelled) {
            const resolved = req;
            setRequests((prev) => {
              if (prev.some((r) => String(r.id) === targetId)) return prev;
              return [resolved, ...prev];
            });
          }
        }
      }

      if (!req || !targetId || cancelled) return;
      applyFocusToRequest(req, targetId);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    highlightRequestId,
    requestResolveHint,
    focusRequestOnly,
    openPaymentEvidence,
    enrichedRequests,
    loading,
    loadingProperties,
    properties,
    openEvidenceModal,
    requestHasPaymentEvidence,
  ]);

  return (
    <div className="property-unit-requests">
      {isFocusMode && (
        <div className="property-unit-requests__focus-bar">
          <button type="button" className="property-unit-requests__focus-back" onClick={exitFocusMode}>
            ← All unit requests
          </button>
          <span className="property-unit-requests__focus-label">Unit request</span>
        </div>
      )}

      {!isFocusMode && (
      <CollapsibleTopPanel
        id="property-unit-requests-top-panel"
        className="property-unit-requests__top-panel"
        defaultOpenOnMobile={false}
      >
        <div className="property-unit-requests__header">
          <h1 className="property-unit-requests__title">{pageTitle}</h1>
          <p className="property-unit-requests__subtitle">
            Tenant requests to rent units on your properties. Review who applied, unit, rent, and status.
          </p>
        </div>

        {!loadingProperties && properties.length > 0 && (
          <div className="property-unit-requests__toolbar">
            <div className="property-unit-requests__field">
              <label htmlFor="pur-property">Property</label>
              <select
                id="pur-property"
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                disabled={loading}
              >
                <option value="all">All properties ({properties.length})</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || p.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="property-unit-requests__field">
              <label htmlFor="pur-status">Status</label>
              <select
                id="pur-status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
              >
                {STATUS_FILTERS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="property-unit-requests__field">
              <label htmlFor="pur-tenant-code-search">Tenant code</label>
              <div className="property-unit-requests__search-group">
                <input
                  id="pur-tenant-code-search"
                  className="property-unit-requests__search-input"
                  type="text"
                  value={tenantCodeFilter}
                  onChange={(e) => setTenantCodeFilter(e.target.value)}
                  placeholder="e.g. TENANT-1234"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="property-unit-requests__toolbar-action-wrap">
              <button
                type="button"
                className="property-unit-requests__button property-unit-requests__button--approve property-unit-requests__toolbar-action"
                onClick={openManualAssignModal}
                disabled={loading}
              >
                Add tenant manually
              </button>
            </div>
          </div>
        )}
      </CollapsibleTopPanel>
      )}

      {loadingProperties ? (
        <div className="property-unit-requests__state">
          <div className="property-unit-requests__spinner" aria-hidden />
          <p>Loading properties…</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="property-unit-requests__state property-unit-requests__state--muted">
          <MdBusiness aria-hidden />
          <p>{emptyPropertiesMessage}</p>
        </div>
      ) : (
        <>
          {error && !approveConfirm.open && !confirmingPaymentsRequestId && !assignTenantModal.open && !evidenceModalRequest && (
            <div className="property-unit-requests__banner property-unit-requests__banner--error" role="alert">
              {error}
            </div>
          )}
          {successMessage && !error && (
            <div className="property-unit-requests__banner property-unit-requests__banner--success" role="status">
              {successMessage}
            </div>
          )}

          {loading && !error ? (
            <div className="property-unit-requests__state">
              <div className="property-unit-requests__spinner" aria-hidden />
              <p>Loading requests…</p>
            </div>
          ) : displayRequests.length === 0 ? (
            <div className="property-unit-requests__state property-unit-requests__state--muted">
              <MdApartment aria-hidden />
              <p>
                {isFocusMode
                  ? 'Could not load this unit request. It may have been updated or removed.'
                  : 'No unit requests match your filters.'}
              </p>
              {isFocusMode && (
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={exitFocusMode}
                >
                  View all unit requests
                </button>
              )}
            </div>
          ) : (
            <div className={`property-unit-requests__grid${isFocusMode ? ' property-unit-requests__grid--focus' : ''}`}>
              {displayRequests.map((req) => (
                <article
                  key={req.id}
                  data-request-id={req.id}
                  className={`property-unit-requests__card${String(req.id) === highlightedRequestId ? ' property-unit-requests__card--highlighted' : ''}`}
                >
                  <div className="property-unit-requests__card-top">
                    <div className="property-unit-requests__card-title-block">
                      <h3 className="property-unit-requests__unit">
                        {req.unitName || req.unitNumber || 'Unit'}
                      </h3>
                      <span className="property-unit-requests__property-name">
                        {req.propertyName || propertyById[req.propertyId] || 'Property'}
                      </span>
                    </div>
                    <span
                      className={`property-unit-requests__badge property-unit-requests__badge--${(req.status || '').toLowerCase()}`}
                    >
                      {formatUnitRequestStatus(req.status)}
                    </span>
                  </div>

                  <div className="property-unit-requests__tenant">
                    <MdPerson aria-hidden />
                    <div className="property-unit-requests__tenant-info">
                      <div className="property-unit-requests__tenant-name">
                        {[req.requesterFirstName, req.requesterLastName].filter(Boolean).join(' ') || 'Tenant'}
                      </div>
                      <div className="property-unit-requests__tenant-email">
                        <MdEmail aria-hidden />
                        {req.requesterEmail || '—'}
                      </div>
                      <div className="property-unit-requests__tenant-chips">
                        {req.requesterTenantCode ? (
                          <span className="pur-chip pur-chip--code">{req.requesterTenantCode}</span>
                        ) : (
                          <span className="pur-chip pur-chip--no-code">No tenant code yet</span>
                        )}
                        {req.bookingCode && (
                          <span className="pur-chip pur-chip--code">Booking: {req.bookingCode}</span>
                        )}
                        {req.requesterKycStatus && (
                          <span className={`pur-chip pur-chip--kyc pur-chip--kyc-${req.requesterKycStatus.toLowerCase()}`}>
                            KYC: {req.requesterKycStatus}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="property-unit-requests__meta-row">
                    <span>
                      <MdCalendarToday aria-hidden />
                      {formatDate(req.createdAt)}
                    </span>
                    <span>
                      <MdAttachMoney aria-hidden />
                      {formatMoneyKES(req.monthlyRent) || '—'}/mo
                    </span>
                    <span className="property-unit-requests__deposit">
                      Deposit {formatMoneyKES(req.depositAmount) || '—'}
                    </span>
                  </div>

                  {/* Score action removed from here as it is now at the top */}

                  <div className="property-unit-requests__actions property-unit-requests__actions--compact">
                    <button
                      type="button"
                      className="property-unit-requests__button property-unit-requests__button--ghost"
                      onClick={() => openEvidenceModal(req)}
                    >
                      View info
                    </button>
                    <button
                      type="button"
                      className="property-unit-requests__button property-unit-requests__button--ghost property-unit-requests__button--chat"
                      onClick={() => openChatModal(req)}
                    >
                      <MdChat aria-hidden />
                      Message
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}

          {!loading && !isFocusMode && filteredRequests.length > 0 && (
            <p className="property-unit-requests__count">
              {filteredRequests.length} request{filteredRequests.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}

      {showRejectModal &&
        rejectingReq &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pur-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Reject request"
            onClick={cancelReject}
          >
            <div className="pur-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__title">Reject request</div>
              <div className="pur-modal__message">
                {isUnitRequestPendingForReject(rejectingReq) ? (
                  <>
                    Reject the request from <strong>{getRequesterName(rejectingReq)}</strong>?
                  </>
                ) : (
                  <>
                    Reject payment evidence from <strong>{getRequesterName(rejectingReq)}</strong>? The tenant
                    can submit new proof; the rental request stays open.
                  </>
                )}
              </div>
              <label className="property-unit-requests__reject-label" htmlFor={`pur-reject-modal-${rejectingReq.id}`}>
                Rejection reason (optional)
              </label>
              <textarea
                id={`pur-reject-modal-${rejectingReq.id}`}
                className="property-unit-requests__reject-textarea"
                value={rejectReasonById?.[rejectingReq.id] || ''}
                onChange={(e) =>
                  setRejectReasonById((prev) => ({ ...prev, [rejectingReq.id]: e.target.value }))
                }
                placeholder="E.g. Unit already taken"
                rows={3}
                disabled={loading || actionRequestId === rejectingReq.id}
                autoFocus
              />
              <div className="property-unit-requests__reject-help">
                {isUnitRequestPendingForReject(rejectingReq)
                  ? 'This will reject the rental request. You can leave the reason blank.'
                  : 'This rejects the submitted payment proof (deposit and/or first month), not the whole request. You can leave the reason blank.'}
              </div>
              <div className="pur-modal__actions">
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={cancelReject}
                  disabled={loading || actionRequestId === rejectingReq.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--danger"
                  onClick={() => confirmReject(rejectingReq)}
                  disabled={loading || actionRequestId === rejectingReq.id}
                >
                  {actionRequestId === rejectingReq.id
                    ? 'Working…'
                    : isUnitRequestPendingForReject(rejectingReq)
                      ? 'Reject request'
                      : 'Reject payment evidence'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <GuestChatModal
        isOpen={Boolean(chatRequest)}
        onClose={() => setChatRequest(null)}
        propertyId={getPropertyIdFromRequest(chatRequest)}
        guestUserId={getRequesterUserIdFromRequest(chatRequest)}
        guestName={getRequesterName(chatRequest || {})}
        propertySubtitle={chatRequest?.propertyName || propertyById[chatRequest?.propertyId] || ''}
        chatMode="landlord"
      />

      {evidenceModalRequest &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pur-modal-overlay pur-modal-overlay--info"
            role="dialog"
            aria-modal="true"
            aria-label="Request information"
            onClick={closeEvidenceModal}
          >
            <div className="pur-modal pur-modal--info" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__title">Request information</div>

              {evidenceModalError && (
                <div
                  className="property-unit-requests__banner property-unit-requests__banner--error"
                  role="alert"
                  style={{ marginTop: 10 }}
                >
                  {evidenceModalError}
                </div>
              )}

              <div className="pur-info-modal__identity">
                <div>
                  <div className="property-unit-requests__payments-label">Tenant</div>
                  <div className="pur-info-modal__name">{getRequesterName(evidenceModalRequest)}</div>
                  <div className="pur-info-modal__muted">{evidenceModalRequest.requesterEmail || '—'}</div>
                </div>
                <span className={`property-unit-requests__badge property-unit-requests__badge--${(evidenceModalRequest.status || '').toLowerCase()}`}>
                  {formatUnitRequestStatus(evidenceModalRequest.status)}
                </span>
              </div>

              <div className="pur-info-modal__quick-actions">
                <button type="button" className="pur-kyc-view-btn pur-score-view-btn" onClick={() => handleViewScore(evidenceModalRequest)}>
                  View Score
                </button>
                <button type="button" className="pur-kyc-view-btn" onClick={() => openKycModal(evidenceModalRequest)}>
                  View KYC
                </button>
              </div>

              <dl className="pur-info-modal__details">
                <div>
                  <dt>Booking code</dt>
                  <dd>{evidenceModalRequest.bookingCode || '—'}</dd>
                </div>
                <div>
                  <dt>Requested</dt>
                  <dd>{formatDate(evidenceModalRequest.createdAt)}</dd>
                </div>
                <div>
                  <dt>Monthly rent</dt>
                  <dd>{formatMoneyKES(evidenceModalRequest.monthlyRent) || '—'}</dd>
                </div>
                <div>
                  <dt>Deposit</dt>
                  <dd>{formatMoneyKES(evidenceModalRequest.depositAmount) || '—'}</dd>
                </div>
                <div>
                  <dt>Payment proof</dt>
                  <dd>{requestHasPaymentEvidence(evidenceModalRequest) ? (paymentsFullyConfirmed(evidenceModalRequest) ? 'Confirmed' : 'Submitted') : 'Not submitted'}</dd>
                </div>
                <div>
                  <dt>Tenant code</dt>
                  <dd>{evidenceModalRequest.requesterTenantCode || '—'}</dd>
                </div>
              </dl>

              {(evidenceModalRequest.notes || evidenceModalRequest.rejectionReason || evidenceModalRequest.reason) && (
                <div className="pur-info-modal__note">
                  <div className="property-unit-requests__payments-label">
                    {String(evidenceModalRequest.status || '').toUpperCase() === 'REJECTED' ? 'Rejection' : 'Note'}
                  </div>
                  {evidenceModalRequest.notes || evidenceModalRequest.rejectionReason || evidenceModalRequest.reason}
                </div>
              )}

              <div className="pur-payments">
                <div className="pur-payments__col">
                  <div className="pur-payments__label">Deposit</div>
                  <div className="pur-payments__value">
                    {formatMoneyKES(evidenceModalRequest.depositPaidAmount || evidenceModalRequest.depositAmount) || '—'}
                  </div>
                  {evidenceModalRequest.depositEvidenceText && (
                    <div
                      className="pur-payments__note property-unit-requests__payments-note--boxed"
                    >
                      {evidenceModalRequest.depositEvidenceText}
                    </div>
                  )}
                  {evidenceModalRequest.depositEvidenceUrl && (
                    <>
                      <div
                        className="property-unit-requests__evidence-preview"
                        onClick={(e) =>
                          openProtectedFile(e, evidenceModalRequest.depositEvidenceUrl, 'Deposit receipt')
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            openProtectedFile(e, evidenceModalRequest.depositEvidenceUrl, 'Deposit receipt');
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <AuthenticatedImage
                          src={evidenceModalRequest.depositEvidenceUrl}
                          alt="Deposit receipt preview"
                          className="property-unit-requests__evidence-preview-img"
                        />
                      </div>
                      <a
                        className="property-unit-requests__payments-link"
                        href={evidenceModalRequest.depositEvidenceUrl}
                        onClick={(e) =>
                          openProtectedFile(e, evidenceModalRequest.depositEvidenceUrl, 'Deposit receipt')
                        }
                      >
                        View deposit photo
                      </a>
                    </>
                  )}
                </div>

                <div className="pur-payments__col">
                  <div className="pur-payments__label">First month</div>
                  <div className="pur-payments__value">
                    {formatMoneyKES(evidenceModalRequest.firstMonthPaidAmount || evidenceModalRequest.monthlyRent) ||
                      '—'}
                  </div>
                  {evidenceModalRequest.firstMonthEvidenceText && (
                    <div
                      className="pur-payments__note property-unit-requests__payments-note--boxed"
                    >
                      {evidenceModalRequest.firstMonthEvidenceText}
                    </div>
                  )}
                  {evidenceModalRequest.firstMonthEvidenceUrl && (
                    <>
                      <div
                        className="property-unit-requests__evidence-preview"
                        onClick={(e) =>
                          openProtectedFile(e, evidenceModalRequest.firstMonthEvidenceUrl, 'First month receipt')
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            openProtectedFile(
                              e,
                              evidenceModalRequest.firstMonthEvidenceUrl,
                              'First month receipt'
                            );
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <AuthenticatedImage
                          src={evidenceModalRequest.firstMonthEvidenceUrl}
                          alt="First month receipt preview"
                          className="property-unit-requests__evidence-preview-img"
                        />
                      </div>
                      <a
                        className="property-unit-requests__payments-link"
                        href={evidenceModalRequest.firstMonthEvidenceUrl}
                        onClick={(e) =>
                          openProtectedFile(e, evidenceModalRequest.firstMonthEvidenceUrl, 'First month receipt')
                        }
                      >
                        View first month photo
                      </a>
                    </>
                  )}
                </div>
              </div>

              <div className="pur-modal__actions">
                {String(evidenceModalRequest.status || '').toUpperCase() === 'REQUESTED' ? (
                  <>
                    <button
                      type="button"
                      className="property-unit-requests__button property-unit-requests__button--approve"
                      onClick={handleEvidenceApprove}
                      disabled={loading || actionRequestId === evidenceModalRequest.id}
                    >
                      {actionRequestId === evidenceModalRequest.id ? 'Working…' : 'Accept'}
                    </button>
                    <button
                      type="button"
                      className="property-unit-requests__button property-unit-requests__button--reject"
                      onClick={handleEvidenceReject}
                      disabled={loading || actionRequestId === evidenceModalRequest.id}
                    >
                      Reject
                    </button>
                  </>
                ) : requestHasPaymentEvidence(evidenceModalRequest) && !paymentsFullyConfirmed(evidenceModalRequest) ? (
                    <>
                      <button
                        type="button"
                        className="property-unit-requests__button property-unit-requests__button--approve"
                        onClick={handleEvidenceConfirmPayments}
                        disabled={loading || actionRequestId === evidenceModalRequest.id}
                      >
                        Confirm payments
                      </button>
                      {String(evidenceModalRequest.status || '').toUpperCase() !== 'PAYMENT_EVIDENCE_SUBMITTED' && (
                        <button
                          type="button"
                          className="property-unit-requests__button property-unit-requests__button--reject"
                          onClick={handleEvidenceReject}
                          disabled={loading || actionRequestId === evidenceModalRequest.id}
                        >
                          Reject
                        </button>
                      )}
                    </>
                ) : (
                  paymentsFullyConfirmed(evidenceModalRequest) && (() => {
                    const alreadyAssigned =
                      Boolean(evidenceModalRequest.assignedAt) ||
                      String(evidenceModalRequest.status || '').toUpperCase() === 'ASSIGNED';
                    if (alreadyAssigned || ['REJECTED', 'MANUAL'].includes(String(evidenceModalRequest.status || '').toUpperCase())) {
                      return null;
                    }
                    return (
                      <button
                        type="button"
                        className="property-unit-requests__button property-unit-requests__button--approve"
                        onClick={() => { closeEvidenceModal(); openAssignTenant(evidenceModalRequest); }}
                        disabled={loading || actionRequestId === evidenceModalRequest.id}
                      >
                        Assign tenant to unit
                      </button>
                    );
                  })()
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {approveConfirm.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pur-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm approval"
            onClick={cancelApproveConfirm}
          >
            <div className="pur-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__title">Approve request</div>
              <div className="pur-modal__message">{approveConfirm.message}</div>
              {error && (
                <div className="property-unit-requests__banner property-unit-requests__banner--error" style={{ marginTop: '16px' }} role="alert">
                  {error}
                </div>
              )}
              <div className="pur-modal__actions">
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={cancelApproveConfirm}
                  disabled={actionRequestId === approveConfirm?.req?.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--approve"
                  onClick={confirmApprove}
                  disabled={loading || actionRequestId === approveConfirm?.req?.id}
                >
                  {actionRequestId === approveConfirm?.req?.id ? 'Working…' : 'Approve'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {confirmingPaymentsReq &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pur-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Confirm payments"
            onClick={cancelConfirmPayments}
          >
            <div className="pur-modal pur-modal--wide" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__title">Confirm payments</div>
              <div className="pur-modal__message">Review the evidence, then tick what’s paid and confirm.</div>
              {confirmingPaymentsReq.bookingCode && (
                <div className="pur-modal__message" style={{ fontWeight: 700, marginTop: 8, color: '#0369a1' }}>
                  Booking code: {confirmingPaymentsReq.bookingCode}
                </div>
              )}
              {error && (
                <div className="property-unit-requests__banner property-unit-requests__banner--error" style={{ marginTop: '16px' }} role="alert">
                  {error}
                </div>
              )}

              <div className="pur-payments">
                <div className="pur-payments__col">
                  <div className="pur-payments__label">Deposit</div>
                  <div className="pur-payments__value">{formatMoneyKES(confirmingPaymentsReq.depositPaidAmount) || '—'}</div>
                  {confirmingPaymentsReq.depositEvidenceText && (
                    <div className="pur-payments__note">{confirmingPaymentsReq.depositEvidenceText}</div>
                  )}
                  {confirmingPaymentsReq.depositEvidenceUrl && (
                    <a
                      className="property-unit-requests__payments-link"
                      href={confirmingPaymentsReq.depositEvidenceUrl}
                      onClick={(e) => openProtectedFile(e, confirmingPaymentsReq.depositEvidenceUrl, 'Photo')}
                    >
                      View photo
                    </a>
                  )}
                  <label className="pur-check">
                    <input
                      type="checkbox"
                      checked={Boolean(confirmPaymentsById?.[confirmingPaymentsReq.id]?.depositPaid)}
                      onChange={(e) =>
                        setConfirmPaymentsById((prev) => ({
                          ...prev,
                          [confirmingPaymentsReq.id]: {
                            ...(prev[confirmingPaymentsReq.id] || {}),
                            depositPaid: e.target.checked,
                          },
                        }))
                      }
                    />
                    Deposit paid
                  </label>
                </div>

                <div className="pur-payments__col">
                  <div className="pur-payments__label">First month</div>
                  <div className="pur-payments__value">{formatMoneyKES(confirmingPaymentsReq.firstMonthPaidAmount) || '—'}</div>
                  {confirmingPaymentsReq.firstMonthEvidenceText && (
                    <div className="pur-payments__note">{confirmingPaymentsReq.firstMonthEvidenceText}</div>
                  )}
                  {confirmingPaymentsReq.firstMonthEvidenceUrl && (
                    <a
                      className="property-unit-requests__payments-link"
                      href={confirmingPaymentsReq.firstMonthEvidenceUrl}
                      onClick={(e) => openProtectedFile(e, confirmingPaymentsReq.firstMonthEvidenceUrl, 'Photo')}
                    >
                      View photo
                    </a>
                  )}
                  <label className="pur-check">
                    <input
                      type="checkbox"
                      checked={Boolean(confirmPaymentsById?.[confirmingPaymentsReq.id]?.firstMonthPaid)}
                      onChange={(e) =>
                        setConfirmPaymentsById((prev) => ({
                          ...prev,
                          [confirmingPaymentsReq.id]: {
                            ...(prev[confirmingPaymentsReq.id] || {}),
                            firstMonthPaid: e.target.checked,
                          },
                        }))
                      }
                    />
                    First month paid
                  </label>
                </div>
              </div>

              <div className="pur-modal__actions">
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={cancelConfirmPayments}
                  disabled={loading || actionRequestId === confirmingPaymentsReq.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--approve"
                  onClick={() => submitConfirmPayments(confirmingPaymentsReq)}
                  disabled={loading || actionRequestId === confirmingPaymentsReq.id}
                >
                  {actionRequestId === confirmingPaymentsReq.id ? 'Working…' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {assignTenantModal.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="pur-modal-overlay" role="dialog" aria-modal="true" aria-label="Assign tenant" onClick={closeAssignTenant}>
            <div className="pur-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__title">Assign tenant to unit</div>
              <div className="pur-modal__message">
                Select the tenant start date (optional). The tenant is picked automatically from the request.
              </div>
              {error && (
                <div className="property-unit-requests__banner property-unit-requests__banner--error" style={{ marginTop: '16px' }} role="alert">
                  {error}
                </div>
              )}

              <div className="pur-form">
                <label className="pur-form__label">
                  Start date (optional)
                  <input
                    className="pur-form__input"
                    type="date"
                    value={assignTenantModal.startDate}
                    onChange={(e) => setAssignTenantModal((prev) => ({ ...prev, startDate: e.target.value }))}
                    disabled={loading || actionRequestId === assignTenantModal?.req?.id}
                  />
                </label>

                <div className="pur-form__hint">
                  This will assign <strong>{assignTenantModal?.req?.requesterEmail || 'the tenant'}</strong> to{' '}
                  <strong>{assignTenantModal?.req?.unitName || assignTenantModal?.req?.unitNumber || 'the unit'}</strong>.
                </div>
              </div>

              <div className="pur-modal__actions">
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={closeAssignTenant}
                  disabled={loading || actionRequestId === assignTenantModal?.req?.id}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--approve"
                  onClick={submitAssignTenant}
                  disabled={loading || actionRequestId === assignTenantModal?.req?.id}
                >
                  {actionRequestId === assignTenantModal?.req?.id ? 'Working…' : 'Assign tenant'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {manualAssignModal.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div className="pur-modal-overlay" role="dialog" aria-modal="true" aria-label="Add tenant manually" onClick={closeManualAssignModal}>
            <div className="pur-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__title">Add tenant manually</div>
              <div className="pur-modal__message">
                Assign a tenant directly to a unit using the tenant user ID.
              </div>
              {error && (
                <div className="property-unit-requests__banner property-unit-requests__banner--error" style={{ marginTop: '16px' }} role="alert">
                  {error}
                </div>
              )}

              <div className="pur-form">
                <label className="pur-form__label">
                  Property
                  <select
                    className="pur-form__input"
                    value={manualAssignModal.propertyId}
                    onChange={(e) =>
                      setManualAssignModal((prev) => ({
                        ...prev,
                        propertyId: e.target.value,
                        unitId: '',
                      }))
                    }
                    disabled={loading || actionRequestId === 'manual-assign'}
                  >
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name || p.id}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="pur-form__label">
                  Unit
                  <select
                    className="pur-form__input"
                    value={manualAssignModal.unitId}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, unitId: e.target.value }))}
                    disabled={loading || manualAssignLoadingUnits || actionRequestId === 'manual-assign'}
                  >
                    <option value="">{manualAssignLoadingUnits ? 'Loading units…' : 'Select unit'}</option>
                    {(manualAssignUnitsByProperty[manualAssignModal.propertyId] || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}{u.status ? ` (${u.status})` : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="pur-form__label">
                  Search tenant (phone, ID, or name)
                  <input
                    className="pur-form__input"
                    type="text"
                    value={tenantSearchQuery}
                    onChange={(e) => {
                      const nextQuery = e.target.value;
                      setTenantSearchQuery(nextQuery);
                      setManualAssignModal((prev) => ({ ...prev, userId: '' }));
                    }}
                    placeholder="e.g. 0712..., ID number, Irene"
                    disabled={loading || actionRequestId === 'manual-assign'}
                  />
                </label>
                {tenantSearchLoading && (
                  <div className="pur-search-feedback">Searching tenants…</div>
                )}
                {tenantSearchError && (
                  <div className="pur-search-feedback pur-search-feedback--error">{tenantSearchError}</div>
                )}
                {!tenantSearchLoading && !tenantSearchError && tenantSearchQuery.trim() && !manualAssignModal.userId && (
                  <div className="pur-tenant-results">
                    {tenantSearchResults.length === 0 ? (
                      <div className="pur-search-feedback">No matching users found.</div>
                    ) : (
                      tenantSearchResults.map((tenant) => {
                        const fullName = [tenant?.firstName, tenant?.lastName].filter(Boolean).join(' ').trim() || 'Unnamed user';
                        const tenantUserId = tenant?.tenantUserId || '';
                        return (
                          <button
                            key={tenantUserId}
                            type="button"
                            className={`pur-tenant-result ${String(manualAssignModal.userId) === String(tenantUserId) ? 'active' : ''}`}
                            onClick={() => {
                              setManualAssignModal((prev) => ({
                                ...prev,
                                userId: String(tenantUserId || ''),
                              }));
                              setTenantSearchQuery(fullName);
                              setTenantSearchResults([]);
                              setTenantSearchError('');
                            }}
                          >
                            <span>{fullName}</span>
                            <span>{tenant?.tenantCode || 'No tenant code'}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}

                {manualAssignModal.userId && (
                  <div className="pur-form__hint">Tenant selected from search list.</div>
                )}

                <label className="pur-form__label">
                  Start date (optional)
                  <input
                    className="pur-form__input"
                    type="date"
                    value={manualAssignModal.startDate}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, startDate: e.target.value }))}
                    disabled={loading || actionRequestId === 'manual-assign'}
                  />
                </label>

                <label className="pur-form__label">
                  Amount paid (KES)
                  <input
                    className="pur-form__input"
                    type="number"
                    min="0"
                    placeholder="e.g. 30000"
                    value={manualAssignModal.amountPaid}
                    onChange={(e) => setManualAssignModal((prev) => ({ ...prev, amountPaid: e.target.value }))}
                    disabled={loading || actionRequestId === 'manual-assign'}
                  />
                </label>
              </div>

              <div className="pur-modal__actions">
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={closeManualAssignModal}
                  disabled={loading || actionRequestId === 'manual-assign'}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--approve"
                  onClick={submitManualAssign}
                  disabled={loading || actionRequestId === 'manual-assign'}
                >
                  {actionRequestId === 'manual-assign' ? 'Assigning…' : 'Assign tenant'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {kycModal.open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pur-modal-overlay"
            role="dialog"
            aria-modal="true"
            aria-label="Tenant KYC Documents"
            onClick={closeKycModal}
          >
            <div className="pur-modal pur-modal--kyc" onClick={(e) => e.stopPropagation()}>
              <div className="pur-kyc-header">
                <div className="pur-kyc-header__info">
                  <div className="pur-modal__title">
                    {[kycModal.req?.requesterFirstName, kycModal.req?.requesterLastName].filter(Boolean).join(' ') || 'Tenant'}
                  </div>
                  <div className="pur-kyc-meta">
                    {kycModal.req?.requesterEmail && (
                      <span className="pur-kyc-meta__email">{kycModal.req.requesterEmail}</span>
                    )}
                    {kycModal.req?.requesterTenantCode ? (
                      <span className="pur-chip pur-chip--code">{kycModal.req.requesterTenantCode}</span>
                    ) : (
                      <span className="pur-chip pur-chip--no-code">No tenant code yet</span>
                    )}
                    {kycModal.req?.requesterKycStatus && (
                      <span className={`pur-chip pur-chip--kyc pur-chip--kyc-${kycModal.req.requesterKycStatus.toLowerCase()}`}>
                        KYC: {kycModal.req.requesterKycStatus}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="pur-kyc-close"
                  onClick={closeKycModal}
                  aria-label="Close KYC modal"
                >
                  ✕
                </button>
              </div>

              <div className="pur-kyc-images">
                {[
                  { label: 'ID Front', key: 'front' },
                  { label: 'ID Back', key: 'back' },
                  { label: 'Selfie', key: 'selfie' },
                ].map(({ label, key }) => {
                  const imgUrls = kycImageUrls[kycModal.req?.id];
                  const url = imgUrls?.[key];
                  const isLoadingThisReq = kycImagesLoading && !imgUrls;
                  return (
                    <div key={key} className="pur-kyc-img-card">
                      <div className="pur-kyc-img-label">{label}</div>
                      {isLoadingThisReq ? (
                        <div className="pur-kyc-img-placeholder">
                          <div className="property-unit-requests__spinner" style={{ width: 28, height: 28, margin: 0 }} />
                        </div>
                      ) : url ? (
                        <img
                          src={url}
                          alt={label}
                          className="pur-kyc-img"
                          onClick={() => window.open(url, '_blank')}
                          title="Click to open full size"
                        />
                      ) : (
                        <div className="pur-kyc-img-placeholder pur-kyc-img-placeholder--empty">
                          <span>Not available</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="pur-modal__actions" style={{ justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="property-unit-requests__button property-unit-requests__button--ghost"
                  onClick={closeKycModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ─── Tenant Score Modal ─────────────────────────────────────── */}
      {scoreModal.open &&
        scoreModal.req &&
        createPortal(
          <div className="pur-modal-overlay" onClick={closeScoreModal}>
            <div className="pur-modal tenant-score-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pur-modal__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <h2 className="pur-modal__title">Tenant Score Details</h2>
                  <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>
                    {[scoreModal.req.requesterFirstName, scoreModal.req.requesterLastName].filter(Boolean).join(' ') || 'Tenant'}
                  </p>
                </div>
                <button 
                  type="button" 
                  className="property-unit-requests__button property-unit-requests__button--ghost" 
                  onClick={closeScoreModal}
                  style={{ 
                    flex: 'none',
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px'
                  }}
                >
                  ×
                </button>
              </div>

              <div className="tenant-score-modal__body" style={{ padding: '4px 0' }}>
                <div className="tenant-score-toolbar" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: '#475569' }}>Period:</label>
                  <select
                    className="pur-form__input"
                    style={{ width: 'auto', padding: '6px 10px' }}
                    value={scoreModal.monthsBack}
                    onChange={(e) => handleViewScore(scoreModal.req, Number(e.target.value))}
                  >
                    <option value={3}>Last 3 months</option>
                    <option value={6}>Last 6 months</option>
                    <option value={12}>Last 12 months</option>
                    <option value={24}>Last 24 months</option>
                  </select>
                </div>

                {scoreModal.loading ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading score…</div>
                ) : scoreModal.error ? (
                  <div style={{ padding: '20px', background: '#fee2e2', color: '#991b1b', borderRadius: '10px', fontSize: '14px' }}>
                    {scoreModal.error}
                  </div>
                ) : scoreModal.score ? (
                  <div className="tenant-score-view-card">
                    <div className="tenant-score-hero" style={{ textAlign: 'center', padding: '20px', background: '#f8fafc', borderRadius: '12px', marginBottom: '16px' }}>
                      <div style={{ fontSize: '48px', fontWeight: 800, color: '#0474C4' }}>
                        {Number(scoreModal.score.tenantScore || 0).toFixed(1)}
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e3a8a', marginTop: '4px' }}>
                        {(() => {
                          const s = scoreModal.score.tenantScore;
                          if (s >= 80) return 'Excellent';
                          if (s >= 65) return 'Good';
                          if (s >= 50) return 'Fair';
                          if (s >= 35) return 'Needs work';
                          return 'At risk';
                        })()}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                      <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Months Counted</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>{scoreModal.score.totalMonthsCounted || 0}</div>
                      </div>
                      <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>On-Time</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#16a34a' }}>{scoreModal.score.onTimeCount || 0}</div>
                      </div>
                      <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Late</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#ea580c' }}>{scoreModal.score.lateCount || 0}</div>
                      </div>
                      <div style={{ padding: '12px', background: '#f1f5f9', borderRadius: '10px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Missed</div>
                        <div style={{ fontSize: '16px', fontWeight: 700, color: '#dc2626' }}>{scoreModal.score.missedCount || 0}</div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default PropertyUnitRequestsView;
